import SearchField from "@/components/SearchField/SearchField";
import { useAppDispatch, useAppSelector } from "@/config/store";
import { Card, List, Pagination } from "@/components/ui";
import { IEdoFile } from "@/shared/model/edo-file.model";
import { getCurrentUserFiles } from "@/shared/reducers/entities/edo-file.reducer";
import { EntityState } from "@/shared/reducers/reducer.utils";
import { ITEMS_PER_PAGE } from "@/shared/util/pagination.constants";
import { FileText, Plus } from "lucide-react";
import React, { createRef, useEffect, useState } from "react";
import { IPaginationBaseState } from "react-jhipster";
import { toast } from "react-toastify";
import axios, { AxiosError } from "axios";
import DeleteFile from "./components/DeleteFile/DeleteFile";
import EditFile from "./components/EditFile/EditFile";
import FileRow from "./components/FileRow/FileRow";
import UploadFiles from "./components/UploadFiles/UploadFiles";

const ArchivioFiles = () => {
  const dispatch = useAppDispatch();
  const fileInput = createRef<HTMLInputElement>();
  const {
    entities: edoFileList,
    updateSuccess,
    loading,
    totalItems,
  } = useAppSelector<EntityState<IEdoFile>>((state) => state.edoFile);
  const [paginationState, setPaginationState] = useState<IPaginationBaseState>({
    activePage: 1,
    itemsPerPage: ITEMS_PER_PAGE,
    sort: "createdAt",
    order: "DESC",
  });
  const [searchValue, setSearchValue] = useState<string>("");
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
  const [filesUploadProgress, setFilesUploadProgress] = useState<number>(0);
  const [uploading, setUploading] = useState<boolean>(false);
  const [editFileDialog, setEditFileDialog] = useState<{
    open: boolean;
    entity?: IEdoFile | null;
  }>({ open: false, entity: null });
  const [deleteFileDialog, setDeleteFileDialog] = useState<{
    open: boolean;
    entity?: IEdoFile | null;
  }>({ open: false, entity: null });

  const handleGetEdoFileList = () => {
    const params = new URLSearchParams();
    if (searchValue) params.append("search", searchValue);
    params.append("filter.repository", "$eq:1");
    dispatch(
      getCurrentUserFiles({
        page: paginationState.activePage,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
        filters: params.toString(),
      })
    );
  };

  useEffect(() => {
    handleGetEdoFileList();
  }, [
    searchValue,
    paginationState.activePage,
    paginationState.order,
    paginationState.sort,
    paginationState.itemsPerPage,
  ]);

  useEffect(() => {
    if (updateSuccess) {
      handleGetEdoFileList();
    }
  }, [updateSuccess]);

  const handlePagination = (currentPage: number) =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleRowsPerPage = (rowsPerPage: number) =>
    setPaginationState({
      ...paginationState,
      itemsPerPage: rowsPerPage,
      activePage: 1,
    });

  const selectFiles = () => {
    fileInput.current?.click();
  };

  const handleUpload = async () => {
    if (filesToUpload.length) {
      setUploading(true);
      const formData = new FormData();
      for (let i = 0; i < filesToUpload.length; i++) {
        formData.append("files", filesToUpload[i], filesToUpload[i].name);
      }
      try {
        await axios.post<{ data: IEdoFile[] }>("/files/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress(progressEvent) {
            const percentage = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total ?? 1)
            );
            setFilesUploadProgress(percentage);
          },
        });
        setFilesToUpload([]);
        handleGetEdoFileList();
      } catch (e) {
        if (e instanceof AxiosError) {
          toast.error(e.response?.data?.message);
          return;
        }
        toast.error("Errore durante l'upload dei file");
      }
      setUploading(false);
    }
  };

  const handleFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    setFilesToUpload(files);
  };

  return (
    <>
      <Card className="overflow-hidden rounded-3xl border border-[var(--color-mockup-border)] shadow-card bg-surface p-0">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 border-b border-[var(--color-mockup-border)] p-4 bg-[var(--color-surface-secondary)]/30">
          <div className="w-full sm:max-w-xs">
            <SearchField
              placeholder="Cerca un file"
              value={searchValue}
              setSearchValue={setSearchValue}
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={selectFiles}
              className="flex shrink-0 items-center gap-1.5 rounded-full bg-[var(--color-mockup-teal)] px-4 py-2 text-xs font-bold text-white shadow-md transition-transform hover:scale-105 active:scale-95"
            >
              <Plus className="h-4 w-4" />
              Aggiungi file
            </button>
            <input
              type="file"
              ref={fileInput}
              name="files"
              multiple
              className="hidden"
              onChange={handleFiles}
            />
            <UploadFiles
              handleUpload={handleUpload}
              setFilesToUpload={(files: File[]) => setFilesToUpload(files)}
              filesToUpload={filesToUpload}
              progress={filesUploadProgress}
              uploading={uploading}
            />
          </div>
        </div>
        {loading && (
          <div className="flex justify-center py-8">
            <span className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        )}
        <div>
          {edoFileList.length ? (
            <List className="divide-y divide-[var(--color-mockup-border)]">
              {edoFileList.map((file) => (
                <FileRow
                  key={file.id}
                  file={file}
                  onEdit={() => setEditFileDialog({ open: true, entity: file })}
                  onDelete={() =>
                    setDeleteFileDialog({ open: true, entity: file })
                  }
                />
              ))}
            </List>
          ) : (
            !loading && (
              <div className="px-6 py-12 text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                  <FileText className="h-5 w-5 text-primary" />
                </div>
                <p className="text-sm font-medium text-[var(--color-mockup-text-mid)]">
                  Nessun file trovato.
                </p>
              </div>
            )
          )}
        </div>
        <Pagination
          count={totalItems}
          page={paginationState.activePage - 1}
          rowsPerPage={paginationState.itemsPerPage}
          onPageChange={(p) => handlePagination(p + 1)}
          onRowsPerPageChange={handleRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} di ${count !== -1 ? count : "more than" + to}`
          }
        />
      </Card>
      <EditFile
        file={editFileDialog.entity ?? undefined}
        open={editFileDialog.open}
        onClose={() => setEditFileDialog({ open: false, entity: null })}
      />
      <DeleteFile
        file={deleteFileDialog.entity ?? ({} as IEdoFile)}
        open={deleteFileDialog.open}
        onClose={() => setDeleteFileDialog({ open: false, entity: null })}
      />
    </>
  );
};

export default ArchivioFiles;
